export enum KycFormStep {
  COMPLETED = 0,
  CLOSE_FORM = "closeForm",
  CLOSE_FORM_AUTHENTICATION = "closeForm_authentication",
  SUCCESS = "success",
  PERSONAL_INFORMATION_FORM = "personalInformationForm",
  CONTACT_INFORMATION_FORM = "contactInformationForm",
  INCOME_INFORMATION_FORM = "incomeInformationForm",
  EARNING_EXPENSE_FORM = "earningExpenseForm",
  POLITICAL_AFFECTED_FORM = "politicalEffectedForm",
  POLITICAL_AFFECTED_PEOPLE_FORM = "politicalEffectedPeopleForm",
  CONFIRMATION_FORM = "confirmationForm",
  INITIALIZING = "initializing",
  IDENTIFICATION_FORM = "identificationForm",
  SIGN_DOCUMENT_FORM = "signDocumentForm",
}
export type TKnowYourCustomerStepAction = "next" | "previous" | "close";

export interface IKnowYourCustomerCurrentForm {
  currentForm: KycFormStep;
}

export enum KycState {
  INITIALIZED = "initialized",
  INITIALIZED_EXPIRED = "initializedExpired",
  PARTIAL_FILLED = "partialFilled",
  SIGNING_REQUIRED = "signingRequired",
  COMPLETED = "completed",
  COMPLETED_PAYMENT_RESTRICTED = "completedPaymentRestricted",
}

export interface IKnowYourCustomerOverview {
  hasQuestionnaire: boolean;
  knowYourCustomerQuestionnaireInformation: {
    state: KycState;
    fillBefore: string;
  };
}

export interface IPersonalInformation {
  firstName: string;
  lastName: string;
  personalCode: string | null;
  dateOfBirth: Date | null;
  citizenshipId: number;
  secondCitizenshipId?: number | null;
  secondPersonalCode: string | null;
  secondDateOfBirth: Date | null;
  selectedPaymentPurposeTypes: string[];
  paymentPurposeDescription: string;
  step?: TKnowYourCustomerStepAction;
  residencyPersonalCode?: string;
  paymentPurposeTypes?: {
    paymentPurposeType: string;
    shouldFillOut: boolean;
  }[];
  citizenships?: {
    citizenshipId: number;
    name: string;
    personalCodeExists: boolean;
    countryCode: string;
  }[];
}

export interface IContactInformation {
  registrationInformation: IContactInformationAddress;
  useSameAddressAsRegistration: boolean;
  placeOfResidence: IPlaceOfResidenceContactInformationAddress;
  countryCodes?: ICountryCode[];
  step?: TKnowYourCustomerStepAction;
}

interface ICountryCode {
  code: string;
  freeLine: boolean;
  name: string;
}

export interface IContactInformationAddress {
  countryCode: string;
  municipalityId: number;
  settlementId: number;
  settlement: string;
  streetId: number;
  street: string;
  houseNumber: string;
  apartmentNumber: string;
  email: string;
  phoneNumber: string;
  step?: TKnowYourCustomerStepAction;
}

export interface IPlaceOfResidenceContactInformationAddress {
  countryCode: string;
  municipalityId: number;
  settlementId: number;
  settlement: string;
  streetId: number;
  street: string;
  houseNumber: string;
  apartmentNumber: string;
  step?: TKnowYourCustomerStepAction;
}

export enum PositionType {
  EMPLOYED_WORKER = "employedWorker",
  BUSINESS_OWNER_PARTICIPANT = "businessOwnerParticipant",
  SELF_EMPLOYED_PERSON = "selfEmployedPerson",
  UNEMPLOYED = "unemployed",
  RETIRED = "retired",
  UNIVERSITY_STUDENT = "universityStudent",
  SCHOOL_STUDENT = "schoolStudent",
  OTHER = "other",
}

export interface IPosition {
  positionType: PositionType;
  shouldFillOut: boolean;
  mustHaveActivityType: boolean;
}

export enum ActivityType {
  INDUSTRY = "actvIndustry",
  TRADE = "actvTrade",
  TRANSPORT = "actvTransport",
  FINANCIAL = "actvFinancial",
  EDUCATION = "actvEducation",
  PROPERTY = "actvProperty",
  IT = "actvIt",
  SERVICES = "actvServices",
  HEALTHCARE = "actvHealthcare",
  PUBLIC = "actvPublic",
  AGRICULTURE = "actvAgriculture",
  CONSTRUCTION = "actvConstruction",
  OTHER = "actvOther",
}

export interface IActivity {
  activityType: ActivityType;
  shouldFillOut: boolean;
}

export enum IncomeSourceType {
  SALARY = "incomeSalary",
  DIVIDEND = "incomeDividend",
  INTEREST = "incomeInterest",
  REAL_ESTATE = "incomeRealEstate",
  FINANCE = "incomeFinance",
  PENSION = "incomePension",
  SOCIAL_AID = "incomeSocialAid",
  SCHOLAR = "incomeScholar",
  RELATIVES = "incomeRelatives",
  OTHER = "incomeOther",
}

export interface IIncomeSource {
  incomeSourceType: IncomeSourceType;
  shouldFillOut: boolean;
}

export interface IIncomeInformation {
  selectedPositionTypes: PositionType[];
  positionTypeDescription: string;
  activityType?: ActivityType;
  activityTypeDescription: string;
  incomeSourceType?: IncomeSourceType;
  incomeSourceTypeDescription: string;
  step?: TKnowYourCustomerStepAction;
  positionTypes?: IPosition[];
  activityTypes?: IActivity[];
  incomeSourceTypes?: IIncomeSource[];
}

export interface IEarningExpense {
  monthlyEarning: string;
  monthlyPayment: string;
  monthlyEarnings?: string[];
  monthlyPayments?: string[];
  step?: TKnowYourCustomerStepAction;
}

export interface IPoliticalEffected {
  isPoliticalEffected: boolean;
  step?: TKnowYourCustomerStepAction;
}

export interface IPoliticalEffectedPeople {
  politicalEffectedPeople: {
    firstName: string;
    lastName: string;
    dateOfBirth: Date | null;
    countryCode: string;
    institution: string;
    responsibilities: string;
    relationType: string | null;
    politicalRole: string | null;
    workingFrom: Date | null;
    workingTo: Date | null;
  }[];
  relationTypes?: string[];
  politicalRoles?: string[];
  countryCodes?: ICountryCode[];
  step?: TKnowYourCustomerStepAction;
}

export interface IKycFormStepResponse {
  currentForm: KycFormStep;
}

export interface IAuthenticationTypes {
  document?: string;
  authenticationTypes?: TAuthenticationType[];
  documentSigningTypes?: TAuthenticationType[];
  authenticationFailure?: {
    identificationFailureTypeId: string;
  } | null;
  wasSignedWithBiometrics?: boolean;
}

export interface IAuthenticationTypeRules {
  isDataRequired: boolean;
  dataRequired?: {
    personalCodeRequired: boolean;
    phoneNumberRequired: boolean;
  };
}

export interface IAuthenticationDataRequest {
  personalCode?: string;
  phoneNumber?: string;
}

export interface IAuthenticationDataResponse {
  verificationCode?: string;
  sessionId?: string;
  redirectUrl?: string;
}

export type TAuthenticationType = "smartId" | "mobileId" | "biometric";

export type TAuthenticationFormType =
  | TAuthenticationType
  | "authenticationType"
  | "documentSigning";

export enum AuthenticationTypeSlug {
  "smartId" = "smart-id",
  "mobileId" = "mobile-id",
  "biometric" = "biometric",
}
